import { Popover, PopoverProps } from 'antd';
import { Store } from 'components/StoreScopes';
import { useMemo } from 'react';
import moreSvg from '../assets/more.svg';
import { useTranslate } from '@tolgee/react';
import toLowerCase from 'utils/toLowerCase';
import { regionTimeZoneMap } from 'services/config/region';
import dayjs from 'lib/dayjs';
import { uniq } from 'lodash-es';

export function useActivityTypeLabel() {
  const { t } = useTranslate();
  const activityTypeLabel = useMemo(() => {
    return {
      LIVE_STREAMING_ACTIVITY: t('直播引流'),
      NEW_PRODUCT_ONSALE_ACTIVITY: t('新品上架'),
      CLEARANCE_SALE_ACTIVITY: t('清仓促销'),
      RECOMMEND_HOT_PRODUCT_ACTIVITY: t('推荐爆款商品'),
      DISCOUNT_ACTIVITY: t('limit_time_discount_items'),
      FLASH_ACTIVITY: t('shangou'),
      VOUCHER_GIFT_ACTIVITY: t('gift_voucher'),
      MULTI_PACK_DISCOUNT_ACTIVITY: t('multi_items_discount'),
      RECOMMEND_ACTIVITY: t('items_recommend'),
    };
  }, [t]);

  return {
    t,
    activityTypeLabel,
  };
}

export function stores2storeScopes(
  stores: Pick<Knowledge.Activity.Store, 'platform' | 'region' | 'storeId'>[],
) {
  return stores.reduce<General.StoreScope[]>((res, cur) => {
    const { platform, region, storeId } = cur;

    const platformScope = res.find((_) => _.platform === platform);

    if (!platformScope) {
      res.push({
        platform,
        regionStoreScopes: [
          {
            region,
            storeIds: [storeId],
            storeCount: 1,
          },
        ],
        storeCount: 1,
      });
      return res;
    }

    const regionScope = platformScope.regionStoreScopes.find((_) => _.region === region);

    if (!regionScope) {
      platformScope.regionStoreScopes.push({
        region,
        storeIds: [storeId],
        storeCount: 1,
      });
      platformScope.storeCount += 1;
      return res;
    }

    regionScope.storeIds = uniq([...(regionScope.storeIds || []), storeId]);
    regionScope.storeCount += 1;
    platformScope.storeCount += 1;

    return res;
  }, []);
}

export function storeScopes2stores(
  storeScopes: General.StoreScope[],
  storeList: Store[] = [],
  storeMap: Partial<Record<number, Store>> = {},
) {
  function storeTransfer({ externalType, region, id }: Store) {
    return {
      platform: externalType,
      region,
      storeId: id,
    };
  }

  return storeScopes.reduce<Knowledge.Activity.Store[]>((res, cur) => {
    const { platform, regionStoreScopes } = cur;

    if (platform === 'WA_BIZ') {
      return res;
    }

    return regionStoreScopes.reduce((res, cur) => {
      const { region, storeIds } = cur;

      if (region === 'all') {
        const stores = storeList.filter((_) => _.externalType === platform).map(storeTransfer);

        res.push(...stores);

        return res;
      }

      if (!storeIds || !storeIds.length) {
        const stores = storeList
          .filter((_) => _.externalType === platform && _.region === region)
          .map(storeTransfer);

        res.push(...stores);

        return res;
      }

      const stores = storeIds.map((id) => {
        return {
          platform,
          region,
          storeId: id,
        };
      });

      res.push(...stores);

      return res;
    }, res);
  }, []);
}

export function Preview(props: PopoverProps) {
  return (
    <Popover {...props}>
      <img src={moreSvg} alt="more" className="w-[16px] ml-[4px] cursor-pointer" />
    </Popover>
  );
}

export function getWithExtra(activity: Knowledge.Activity.Detail) {
  const { activityFrom, activityTo, stores } = activity;

  const regions = Object.keys(
    stores.reduce<Partial<Record<General.UpperCaseRegion, true>>>((res, { region }) => {
      res[region] = true;
      return res;
    }, {}),
  );

  const regionTimeZones = regions.map((region) => regionTimeZoneMap[toLowerCase(region)]);

  const now = dayjs();

  const status: Knowledge.Activity.WithExtra['status'] = regionTimeZones.every((timeZone) =>
    dayjs.tz(activityTo, timeZone).isBefore(now, 'minutes'),
  )
    ? 'expired'
    : regionTimeZones.every((timeZone) => dayjs.tz(activityFrom, timeZone).isAfter(now, 'minutes'))
    ? 'waiting'
    : 'inprogress';

  return {
    ...activity,
    regions,
    status,
  };
}
